/*
 * fn: buildRoute
 *
 * Builds a string URL from a given route object
 *
 * Options:
 * - clearOptionalFilters flag builds the route with no optional filters
 *
 */
import { getRouteConfig, getRouteParamConfig, isRouteParamType, type RouteType } from '@/defs/route'

import { buildRouteParam } from './buildRouteParam'
import { getRouteTab } from './getRouteTab'
import { getRouteTemplateSegments } from './getRouteTemplateSegments'

export type OptionsProps = {
  clearOptionalFilters?: boolean
}

export const buildRoute = (route: RouteType, options?: OptionsProps) => {
  const path: string[] = []
  const searchParams = new URLSearchParams()

  const { queryParams, template } = getRouteConfig(route.section)

  for (const { type, value } of getRouteTemplateSegments(template)) {
    if (type === 'literal') {
      if (value) path.push(value)
      continue
    }

    if (!isRouteParamType(value)) {
      console.error('[Route Util] Segment is not a known route param', value)
      return
    }

    const { isFilter } = getRouteParamConfig(value)
    if (options?.clearOptionalFilters && isFilter && type !== 'required') continue

    const item = buildRouteParam(value, route[value])

    if (item) {
      path.push(item)
    } else if (type === 'required') {
      console.error(`[Route Util] Segment "${value}" is required but was not applied for template: "${template}"`)
      return
    }
  }

  /* A section with tabs takes its tab as an optional trailing path segment */
  const tab = getRouteTab(route, route.tab)
  if (tab?.route.tab) path.push(tab.route.tab)

  for (const param of queryParams || []) {
    const { isFilter, query: queryKey } = getRouteParamConfig(param)
    if (!queryKey || (options?.clearOptionalFilters && isFilter)) continue

    const item = buildRouteParam(param, route[param])
    if (item) searchParams.append(queryKey, item)
  }

  const query = searchParams.toString()

  return `/${path.join('/')}${query ? `/?${query}` : ''}`
}
